import "reflect-metadata";
import React, { useContext, createContext } from "react";
import { Container, interfaces, METADATA_KEY } from "inversify";

const DIContext = createContext<Container | null>(null);

export const DependencyInjectionProvider: React.FC<{
  container: Container;
  children?: React.ReactNode;
}> = ({ container, children }) => {
  return <DIContext.Provider value={container}>{children}</DIContext.Provider>;
};

//eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useDependencyInjector = <T extends unknown>(service: interfaces.ServiceIdentifier<T>) => {
  const container = useContext(DIContext);
  if (!container) {
    throw new Error("Missing DI container");
  }
  return container.get<T>(service);
};

/**
 * Resolves a dependency only if it is bound. Returns the instance when bound, null otherwise.
 * Use when the component must work with or without the dependency (e.g. optional edit-columns store).
 */
//eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useOptionalDependencyInjector = <T extends unknown>(service: interfaces.ServiceIdentifier<T>): T | null => {
  const container = useContext(DIContext);
  if (!container || !container.isBound(service)) {
    return null;
  }
  return container.get<T>(service);
};

/**
 * Instantiates a new class instance with all the injectable dependencies using decorator metadata.
 * The instance will be created once when the component first mounts
 * The class to-be-instantiated needs to have the param types metadata available (AFAIK @injectable adds them, there might be a better decorator for that)
 *
 * Useful when creating ephemeral local stores that depend on global stores to prevent memory leaks
 * If the store has to clean up eventListeners or mobx-reactions created inside the store, add a method called dispose() to the class, and it will be called automatically when the component unmounts.
 *
 * @example
 * const myLocalStore = useLocalStoreWithInjectedDependencies(MyLocalStore);
 * */
// We use our own memoization instead of useMemo because react runs useMemo twice in strict mode
// so it can side effects. this hook is intended to be used to exit react world to the mobx world
// and we dont want a store to be created twice.
const memo = new WeakMap();
export const useLocalStoreWithInjectedDependencies = <T extends object>(Class: interfaces.Newable<T>): T => {
  const container = useContext(DIContext);
  if (!container) {
    throw new Error("useLocalStoreWithInjectedDependencies missing DI container in context");
  }
  const store = React.useMemo(() => {
    const memoKey = (React as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.ReactCurrentOwner?.current;
    if (memo.has(memoKey)) {
      return memo.get(memoKey);
    }
    // Fallback to empty array - as this will be undefined when no constructor or no args in constructor.
    const dependencyClasses = Array.from<[{ key: string; value: any }] | undefined>({
      ...Reflect.getMetadata(METADATA_KEY.TAGGED, Class),
      length: Class.length,
    }).filter((dependency) => dependency !== undefined);
    const dependencyConstructorParams = dependencyClasses.map((data, index) => {
      if (!data) {
        return undefined;
      }
      const [{ key, value }] = data;
      try {
        return value && container.get(value);
      } catch (e) {
        const errorMessage = `Failed to find dependency for: ${key}, param index:${index}, dependencyClasses: ${String(dependencyClasses)}`;
        if (import.meta.env.MODE !== "production") {
          // eslint-disable-next-line no-console
          console.error(errorMessage, e);
        }
        return undefined;
      }
    });
    const result = new (Class as any)(...dependencyConstructorParams) as T;
    if (memoKey) {
      memo.set(memoKey, result);
    }
    return result;
  }, [Class, container]);
  React.useEffect(() => {
    return () => (store as any).dispose?.();
  }, [store]);
  return store;
};
